import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

// Extracted at build time by scripts/generate-snap-routes.js
// (VERTICAL_FAQ_FILES) into the prerendered FAQPage schema, so every answer
// here is published twice. Keep each one inside the claim limits: no speed
// claims, no accuracy numbers, no "every file", OCR scoped to Analyze
// contents on Mac and Windows, Offline Mode as the opt-in for firms that
// can't use a cloud AI.
const LAW_FIRMS_FAQS = [
  {
    question: 'How does Sortio decide which client and matter a document belongs to?',
    answer:
      "With Analyze contents on, Sortio reads an excerpt of each document (running on-device OCR first if it's a scan) and picks out the people, organizations, dates and document type. It compares those with the folder structure you gave it, or with your Clio matters, and proposes a folder and a new name for each file along with the reason. You review the plan, fix any it got wrong, and nothing moves until you click Apply.",
  },
  {
    question: 'Will my privileged client documents leave my computer?',
    answer:
      "It depends on the AI you choose. By default Sortio uses OpenAI's cloud AI models through Sortio's API: they receive filenames and folder paths, and, if you turn on Analyze contents, text from each document, up to an excerpt of about 4 KB. OCR for scans runs on your computer, and the scanned image is not sent. To keep document text away from a cloud AI, use Offline Mode (Ollama or LM Studio), which runs the model on your own computer. To use a cloud AI under your firm's own contract, use your own API key (BYOK), which talks directly to your provider. Uploads to Clio go from your computer to Clio.",
  },
  {
    question: 'I have a folder of mixed scans from multiple clients. Can Sortio split them up?',
    answer:
      "Yes. Point Sortio at the folder, tell it how your firm files, and click Analyze contents. Scanned PDFs with no text layer get on-device OCR on Mac and Windows (OCR isn't available on Linux). OCR reads the first pages of each scan, which usually carry the caption, the parties and the date. Each file is placed on its own, so scan_001.pdf through scan_200.pdf can be split across several clients. Scans it can't read are set aside in a _Needs Review folder rather than guessed.",
  },
  {
    question: 'What if a file could plausibly belong to two open matters with similar names?',
    answer:
      "Check the preview. It shows where each file is going and why, so a document headed for the wrong Smith matter is visible before you apply. Click the pencil to change its folder, then apply. Naming matter folders after the case caption (for example 2026-014 Smith v. State) gives Sortio more to match on. In the Clio flow, files Sortio isn't confident about are left unselected for you to assign.",
  },
  {
    question: 'Does Sortio support Bates numbering or document stamping for discovery?',
    answer:
      "No. Sortio focuses on classification, naming, and filing rather than stamping. It will rename files into the pattern you describe (for example Smith_v_State_2026-03-14_Motion-in-Limine.pdf) so they sort correctly in Clio and in your local discovery folder. For Bates stamps on the page itself, keep using your existing tool; Sortio runs alongside Adobe Acrobat and similar utilities.",
  },
  {
    question: 'Can Sortio file new documents as they arrive?',
    answer:
      "Partly. On Pro, watch folders run rules on new files, so a downloaded court filing can be routed by its filename, type, or the text in a text-based PDF. Rules don't run OCR, so new scans are best filed with a sort: tell Sortio how to file them, click Analyze contents, check the preview, and apply.",
  },
  {
    question: 'Does Sortio redact privileged or sensitive content before upload?',
    answer:
      "No. Sortio does not modify the contents of your files. It reads them to classify and route, then moves, renames, or uploads the originals to Clio exactly as they were. If you need redaction, do it in your PDF tool first, then let Sortio file the redacted version.",
  },
  {
    question: 'What record does Sortio keep of what it moved?',
    answer:
      "Each applied sort is saved in Activity with each file's original location, its new location and its new name, and you can undo a sort from there. Clio uploads are also recorded on your computer, which is how Sortio skips a file it has already uploaded from the same location the next time.",
  },
  {
    question: 'Can I use Sortio with my own AI model or API key for confidentiality reasons?',
    answer:
      "Yes. Sortio supports BYOK (bring your own key) for OpenAI, Anthropic, and other providers, so AI requests go from your computer directly to the provider on your firm's account, not through Sortio. Offline Mode runs a local model with Ollama or LM Studio, so document text stays on your computer. BYOK and Offline Mode are not metered by Sortio and do not count against your AI allowance.",
  },
  {
    question: 'How does Sortio handle our existing client folder structure?',
    answer:
      'Give Sortio your existing client and matter folders as a folder list in the sort options and turn on "Restrict to a folder list". The AI will only file documents into those folders, or into a fallback folder you choose, such as Needs Review, for anything it can\'t place. That keeps the structure your firm has used for years instead of inventing parallel folders.',
  },
  {
    question: 'My firm is on Windows. Does Sortio work there too?',
    answer:
      "Yes. Sortio runs on Mac and Windows (and Linux) at the same pricing. On-device OCR for scans is available on Mac and Windows only, not on Linux. For mixed-OS firms, run Sortio on the machine that holds your scanning workflow.",
  },
  {
    question: 'How large a backlog can Sortio handle in one go?',
    answer:
      'AI sort works best under about 5,000 files per run, so split a large backlog into batches. Reading contents and running OCR on scans takes longer than a filename-only sort. For very large legacy archives, rules route any number of files by name, type, or date without using your AI allowance.',
  },
  {
    question: 'What does Sortio cost for a solo attorney, and is there a way to try it first?',
    answer:
      'Yes. The free trial includes a one-time AI allowance, enough to run Sortio on a real intake folder before paying, and no credit card is required. Pro is $14.99 per month or $99 per year and adds a monthly AI allowance sized for heavy use, the Clio integration, and watch folders. Team plans add shared rules and centralized AI policy for firms that want everyone on the same standard.',
  },
];

const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <div className="mb-4 transition-all duration-300 transform hover:translate-x-1">
    <div className={`apple-panel transition-all duration-300 ${isOpen ? 'ring-1 ring-sortio-primary/30' : ''}`}>
      <button
        className="flex justify-between items-center w-full p-5 text-left rounded-apple focus:outline-none"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="text-base font-medium text-apple-50 pr-4">{question}</span>
        <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors duration-300 ${isOpen ? 'bg-sortio-primary/20' : 'bg-apple-600/40'}`}>
          {isOpen ? (
            <ChevronUp className="w-3.5 h-3.5 text-sortio-primary transition-transform duration-300" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5 text-apple-300 transition-transform duration-300" />
          )}
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="p-5 pt-0">
          <p className="text-apple-300 text-sm leading-relaxed">{answer}</p>
        </div>
      </div>
    </div>
  </div>
);

const LawFirmsFAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: LAW_FIRMS_FAQS.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <section id="law-firms-faq" className="my-16 sm:my-20">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="flex flex-col items-center mb-12">
        <div className="mb-4 rounded-full bg-apple-700/60 backdrop-blur-sm p-2 border border-apple-600/20">
          <HelpCircle className="w-6 h-6 text-sortio-primary" />
        </div>
        <h2 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight text-apple-50 text-center">
          Law firm <span className="apple-gradient-text">FAQ</span>
        </h2>
        <p className="text-apple-300 text-lg font-light max-w-2xl text-center">
          Questions we hear from paralegals, solo attorneys and small firms evaluating Sortio for
          client and matter filing, scans, confidentiality, and Clio.
        </p>
      </div>

      <div className="max-w-3xl mx-auto">
        {LAW_FIRMS_FAQS.map((faq, index) => (
          <FAQItem
            key={index}
            question={faq.question}
            answer={faq.answer}
            isOpen={openIndex === index}
            onToggle={() => setOpenIndex(openIndex === index ? -1 : index)}
          />
        ))}
      </div>
    </section>
  );
};

export default LawFirmsFAQ;
